import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Constants from "../constants";
import { CREATE_SESSION, LOAD_CONFIG, RESET_FOR_MODEL_SELECTOR, SET_IS_LOADING } from "../store/actions/sessionActions";
import { getHasSession } from "../store/sagas/selectors";
import { goToModelSelector } from "../utilities/navigationUtils";

/**
 * Dispatches CREATE_SESSION or LOAD_CONFIG_INTO_EXISTING_SESSION when the configurator or summary route is shown with config_string in the URL.
 * When navigating to model selector, clears configurator state (RESET_FOR_MODEL_SELECTOR) and keeps session.
 */
export function SessionInitFromUrl() {
    const location = useLocation();
    const dispatch = useDispatch();
    const hasSession = useSelector(getHasSession);

    useEffect(() => {
        // Normalize pathname for robust matching (RR v6 usually gives leading slash; allow trailing slash).
        const pathname = "/" + (location.pathname || "").replace(/^\/+/, "").replace(/\/+$/, "") || "/";
        const configuratorRegex = new RegExp(`^${Constants.configuratorUrlPath.replace(/\//g, "\\/")}\\/?$`);
        const modelSelectorRegex = new RegExp(`^${Constants.modelSelectorUrlPath.replace(/\//g, "\\/")}\\/?$`);
        const summaryRegex = new RegExp(`^${Constants.summaryUrlPath.replace(/\//g, "\\/")}\\/?$`);
        const isConfiguratorRoute = pathname === "/" || configuratorRegex.test(pathname);
        const isModelSelectorRoute = modelSelectorRegex.test(pathname);
        const isSummaryRoute = summaryRegex.test(pathname);

        if (isModelSelectorRoute) {
            dispatch({ type: SET_IS_LOADING, isLoading: false });
            dispatch({ type: RESET_FOR_MODEL_SELECTOR });
            return;
        }

        const searchParams = new URLSearchParams(location.search);
        const initialConfig = searchParams.get("config_string") || searchParams.get("encoded_config");
        const configEncoding = searchParams.get("enc_type");
        const surfaceProductArticleNo = searchParams.get("surface_product");

        if (isSummaryRoute) {
            if (initialConfig) {
                dispatch({ type: SET_IS_LOADING, isLoading: true });
                if (hasSession) {
                    dispatch({
                        type: LOAD_CONFIG,
                        initialConfig,
                        configEncoding: configEncoding || undefined,
                        surfaceProductArticleNo: surfaceProductArticleNo || undefined,
                    });
                } else {
                    dispatch({
                        type: CREATE_SESSION,
                        initialConfig,
                        configEncoding,
                        surfaceProductArticleNo,
                    });
                }
            } else if (!hasSession) {
                goToModelSelector(undefined, location.search);
            } else {
                dispatch({ type: SET_IS_LOADING, isLoading: false });
            }
            return;
        }

        if (initialConfig && isConfiguratorRoute) {
            dispatch({ type: SET_IS_LOADING, isLoading: true });
            if (hasSession) {
                dispatch({
                    type: LOAD_CONFIG,
                    initialConfig,
                    configEncoding: configEncoding || undefined,
                    surfaceProductArticleNo: surfaceProductArticleNo || undefined,
                });
            } else {
                dispatch({
                    type: CREATE_SESSION,
                    initialConfig,
                    configEncoding,
                    surfaceProductArticleNo,
                });
            }
        } else if (isConfiguratorRoute && !initialConfig) {
            dispatch({ type: SET_IS_LOADING, isLoading: false });
            goToModelSelector(undefined, location.search);
        }
    }, [location.pathname, location.search, dispatch, hasSession]);

    return null;
}
